import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noindex?: boolean;
  canonical?: string;
  structuredData?: object;
}

export const SEO = ({
  title = 'Nazandoo - Marketplace e-commerce Kinshasa',
  description = 'Nazandoo, la marketplace #1 à Kinshasa. Achetez et vendez en ligne en toute sécurité. Retirez dans +100 points relais.',
  keywords = 'marketplace Kinshasa, e-commerce RDC, achat en ligne Congo',
  ogImage = 'https://nazandoo.cd/og-image.png',
  ogType = 'website',
  noindex = false,
  canonical,
  structuredData
}: SEOProps) => {
  const location = useLocation();
  const currentUrl = `https://nazandoo.cd${location.pathname}`;
  const canonicalUrl = canonical || currentUrl;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic SEO tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:type', ogType, true);

    // Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Structured Data (JSON-LD)
    if (structuredData) {
      const scriptId = 'structured-data-script';
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, ogImage, ogType, noindex, currentUrl, canonicalUrl, structuredData]);

  return null;
};

// SEO Presets for common pages
export const SEOPresets = {
  products: {
    title: 'Tous les Produits - Nazandoo Marketplace Kinshasa',
    description: 'Découvrez des milliers de produits sur Nazandoo. Électronique, mode, maison, beauté et plus encore. Livraison en points relais à Kinshasa.',
    keywords: 'produits Kinshasa, acheter en ligne RDC, marketplace produits Congo, shopping Kinshasa',
  },
  
  relayPoints: {
    title: 'Points Relais - Nazandoo | +100 points de retrait à Kinshasa',
    description: 'Trouvez un point relais Nazandoo près de chez vous à Kinshasa. +100 points de retrait sécurisés. Horaires flexibles, service rapide.',
    keywords: 'points relais Kinshasa, retrait colis Kinshasa, livraison proximité Congo',
  },
  
  becomeVendor: {
    title: 'Devenir Vendeur - Nazandoo | Vendez en ligne à Kinshasa',
    description: 'Vendez vos produits sur Nazandoo. Commission 8%, paiement sous 24h, +100 points relais. Rejoignez la marketplace #1 de Kinshasa.',
    keywords: 'vendre en ligne Kinshasa, devenir vendeur Congo, marketplace vendeur RDC, e-commerce Kinshasa',
  },
  
  about: {
    title: 'À Propos - Nazandoo Marketplace | Notre Mission',
    description: 'Nazandoo révolutionne le e-commerce à Kinshasa avec des points relais sécurisés. Découvrez notre mission, nos valeurs et notre équipe.',
    keywords: 'Nazandoo histoire, marketplace Kinshasa mission, e-commerce RDC vision',
  },
  
  howItWorks: {
    title: 'Comment ça Marche - Nazandoo | Guide complet',
    description: 'Guide complet Nazandoo : comment acheter, vendre, utiliser les points relais et payer en ligne. Simple, rapide et sécurisé.',
    keywords: 'guide Nazandoo, comment acheter Kinshasa, tutoriel marketplace Congo',
  },
  
  support: {
    title: 'Support Client - Nazandoo | Aide et FAQ',
    description: 'Centre d\'aide Nazandoo : FAQ, contact support, assistance client. Notre équipe répond sous 24h. Support email, téléphone et chat.',
    keywords: 'support Nazandoo, aide marketplace Kinshasa, contact Nazandoo, FAQ e-commerce',
  },
};

// Product Page SEO generator
export const generateProductSEO = (product: {
  name: string;
  description: string;
  price: number;
  currency: string;
  image_url?: string;
  category?: string;
  vendor_name?: string;
  id: string;
}) => {
  const title = `${product.name} - ${product.price} ${product.currency} | Nazandoo`;
  const description = `${product.description?.substring(0, 150)}... Achetez maintenant sur Nazandoo. Retrait en point relais à Kinshasa.`;
  const keywords = `${product.name}, ${product.category || 'produit'} Kinshasa, acheter ${product.category} Congo`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image_url || 'https://nazandoo.cd/og-image.png',
    brand: {
      '@type': 'Brand',
      name: product.vendor_name || 'Nazandoo Marketplace',
    },
    offers: {
      '@type': 'Offer',
      url: `https://nazandoo.cd/products/${product.id}`,
      priceCurrency: product.currency,
      price: product.price,
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: product.vendor_name || 'Nazandoo Marketplace',
      },
    },
  };

  return {
    title,
    description,
    keywords,
    ogImage: product.image_url || 'https://nazandoo.cd/og-image.png',
    ogType: 'product' as const,
    structuredData,
  };
};

// Category Page SEO generator
export const generateCategorySEO = (categoryName: string, productCount: number) => ({
  title: `${categoryName} - ${productCount} produits | Nazandoo Kinshasa`,
  description: `Découvrez ${productCount} produits de ${categoryName} sur Nazandoo. Achetez en ligne, retirez en point relais à Kinshasa. Paiement sécurisé.`,
  keywords: `${categoryName} Kinshasa, acheter ${categoryName} Congo, marketplace ${categoryName} RDC`,
});
